/**
 * SearchModel — 查询区模型
 *
 * ## 架构角色
 * 管理列表页顶部查询区的状态和搜索/重置行为。
 * 继承 BaseModel，通过事件系统驱动，不直接操作 DOM。
 *
 * ## 核心方法
 *
 * ### search(values) — 执行查询
 * ```
 * beforeSearch → 过滤空值(undefined/null/'') → 存 queryValues → _reloadList → afterSearch
 * ```
 *
 * ### reset() — 重置
 * ```
 * beforeReset → 清空 queryValues → _reloadList(null) → afterReset
 * ```
 *
 * ### initParams(pageParams) — 初始化
 * 缓存页面级 params，后续 _reloadList 复用
 *
 * ## 事件
 * | 事件 | 说明 |
 * |------|------|
 * | beforeSearch | 返回 false 阻止查询 |
 * | afterSearch | 查询完成 |
 * | beforeReset | 返回 false 阻止重置 |
 * | afterReset | 重置完成 |
 *
 * ## 使用示例
 * ```js
 * var searchModel = new ttx.models.SearchModel({});
 * searchModel.initParams({ billno: 'xxx', mode: 'list' });
 * searchModel.on('beforeSearch', function(values) { // 校验
 * searchModel.search({ name: '张三' });
 * ```
 */
ttx.models.register('SearchModel', function (modelType) {
    const searchModel = function (data) {
        ttx.models.BaseModel.call(this, data);
    };
    searchModel.prototype = ttx.utils.getPrototype(ttx.models.BaseModel.prototype);
    searchModel.prototype.modelType = modelType;

    searchModel.prototype.getQueryValues = function () {
        return this._get_data('queryValues') || {};
    };

    /** 初始化：缓存页面级 params，用于后续 reload */
    searchModel.prototype.initParams = function (pageParams) {
        this.setCache('pageParams', pageParams || {});
    };

    /** 重新加载列表数据 */
    searchModel.prototype._reloadList = function (queryValues) {
        var root = this.getRootParent?.() || this;
        var pageParams = this.getCache('pageParams') || root.getParams?.() || {};
        var mergedQuery = Object.assign({}, pageParams.query || {});
        if (queryValues) {
            Object.assign(mergedQuery, queryValues);
        }
        // 确保 metaid 等关键字段不丢失
        if (!mergedQuery.metaid) {
            mergedQuery.metaid = root._get_data?.('template')?.view?.cCardKey || root._get_data?.('template')?.id || '';
        }
        var billno = pageParams.billno || pageParams.billNo || root._get_data?.('serviceCode') || root._get_data?.('template')?.serviceCode || '';
        // 数据过滤：参照等场景在根 VM 缓存 filter（MDF 结构），放请求体顶层交给后端做 SQL WHERE 过滤
        var filter = root.getCache?.('filter') || null;
        var params = { mode: pageParams.mode || 'list', query: mergedQuery };
        // 从缓存读取分页信息（支持分页切换）
        var pagination = root.getCache?.('pagination') || {};
        var pageSize = pagination.pageSize || 20;
        var pageNum = pagination.current || pagination.pageNum || 1;
        var body = { params: params, pageSize: pageSize, pageNum: pageNum };
        if (filter) body.filter = filter;
        ttx.bill.list(null, { billno: billno }, body, function (res) {
            // 错误处理：失败时 err 为 Error 对象（含 code/message），或响应带非 200 code
            var isError = res instanceof Error
                || (res && typeof res === 'object' && 'code' in res && res.code !== 200);
            if (isError) {
                ttx.log?.error?.('[SearchModel] 列表查询失败', res?.message || res);
                ttx.toast?.error?.(res?.message || '查询失败');
                root.execute('dataRefresh');
                return;
            }
            if (res && res.data && (res.data.list || res.data.data)) {
                var list = res.data.list || res.data.data;
                // 兼容命名空间格式：若当前 value 已是按编码隔离的对象，写入对应的表格 key，避免覆盖其他组件数据
                var curValue = root._get_data('value');
                if (curValue && typeof curValue === 'object' && !Array.isArray(curValue)) {
                    var tableKey = null;
                    for (var k in curValue) {
                        if (curValue.hasOwnProperty(k) && Array.isArray(curValue[k])) {
                            tableKey = k;
                            break;
                        }
                    }
                    if (tableKey) {
                        var updated = {};
                        updated[tableKey] = list;
                        root._set_data('value', Object.assign({}, curValue, updated), true);
                    } else {
                        root._set_data('value', list, true);
                    }
                } else {
                    root._set_data('value', list, true);
                }
                // 保留当前页码，不要在每次搜索后重置为 1（分页切换时会丢失选择）
                var prevPagination = root.getCache('pagination') || {};
                root.setCache('pagination', {
                    current: Number(prevPagination.current) || Number(prevPagination.pageNum) || 1,
                    pageSize: pageSize,
                    total: res.data.total || 0
                });
            }
            root.execute('dataRefresh');
        });
    };

    /** 查询：存储查询值，过滤空值，触发事件，重载列表 */
    searchModel.prototype.search = function (values) {
        if (this.execute('beforeSearch', values) === false) return;
        // 过滤掉 undefined/null/空字符串，避免无效参数传到 API
        var cleaned = {};
        if (values) {
            Object.keys(values).forEach(function (k) {
                var v = values[k];
                if (v !== undefined && v !== null && v !== '' && !(Array.isArray(v) && v.length === 0)) {
                    cleaned[k] = v;
                }
            });
        }
        this._set_data('queryValues', cleaned, true);
        this._reloadList(cleaned);
        this.execute('afterSearch', cleaned);
    };

    /** 重置：清空查询值，重载默认列表 */
    searchModel.prototype.reset = function () {
        if (this.execute('beforeReset') === false) return;
        this._set_data('queryValues', {}, true);
        this._reloadList(null);
        this.execute('afterReset');
    };

    return searchModel;
});
